import React from 'react';
import {
    Box,
    Paper,
    Typography,
    CircularProgress,
    Alert,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Grid,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    useTheme,
    useMediaQuery,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { computeDiffHtml, getChangeSummary } from '@/utils/DiffWord';
import { ContentCopy } from '@mui/icons-material';
import { IconButton, Tooltip } from '@mui/material';
import { useSnackbar } from 'notistack';

// Generic audit log interface
export interface AuditLogItem {
    id: string;
    action: string;
    changeSummary?: string;
    summary?: string;
    message?: string;
    oldJson?: string | null;
    changeJson?: string | null;
    createdAt?: string;
    timestamp?: string;
    time?: string;
    updatedAt?: string;
    updatedByUserMail?: string;
    updatedBy?: string;
    user?: string;
    updatedByRole?: string;
    version?: string;
    ipAddress?: string;
    userAgent?: string;
}

export interface AuditHistoryProps {
    title: string;
    entityName?: string;
    backLink?: {
        to: string;
        label: string;
    };
    data?: any;
    isLoading: boolean;
    error?: any;
    emptyMessage?: string;
}

function safeParseJson(input?: string | null): any {
    if (!input) return undefined;
    try {
        return JSON.parse(input);
    } catch {
        return input;
    }
}

export default function AuditHistory({
    title,
    entityName,
    backLink,
    data,
    isLoading,
    error,
    emptyMessage = 'No history found.'
}: AuditHistoryProps) {
    const [selected, setSelected] = React.useState<AuditLogItem | null>(null);
    const { enqueueSnackbar } = useSnackbar();

    const handleCopy = async (text: string) => {
        try {
            await navigator.clipboard.writeText(text);
            enqueueSnackbar('Copied to clipboard!', {
                variant: 'success',
                autoHideDuration: 2000
            });
        } catch (err) {
            enqueueSnackbar('Failed to copy text', {
                variant: 'error',
                autoHideDuration: 2000
            });
        }
    };

    const items: AuditLogItem[] = React.useMemo(() => {
        if (!data) return [];
        if (Array.isArray(data)) return data;
        const d: any = data;
        return (d?.data ?? d?.logs ?? d?.items ?? []);
    }, [data]);

    const handleOpen = (item: AuditLogItem) => setSelected(item);
    const handleClose = () => setSelected(null);

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    return (
        <Box sx={{ p: isMobile ? 2 : 3 }}>
            <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: isMobile ? 'flex-start' : 'center',
                mb: 2,
                flexDirection: isMobile ? 'column' : 'row',
                gap: isMobile ? 1 : 0
            }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold', fontSize: isMobile ? '20px' : '24px' }}>
                    {entityName ? `${entityName} — ${title}` : title}
                </Typography>
                {backLink && (
                    <Typography
                        component={RouterLink}
                        to={backLink.to}
                        sx={{
                            textDecoration: 'none',
                            fontSize: isMobile ? '14px' : '16px',
                            alignSelf: isMobile ? 'flex-start' : 'auto'
                        }}
                        color="primary"
                    >
                        {backLink.label}
                    </Typography>
                )}
            </Box>

            {isLoading && (
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <CircularProgress size={20} />
                    <Typography>Loading history...</Typography>
                </Box>
            )}

            {!!error && <Alert severity="error">Failed to load history</Alert>}

            {!isLoading && !error && (
                items.length === 0 ? (
                    <Typography>{emptyMessage}</Typography>
                ) : isMobile ? (
                    // Mobile Card Layout
                    <Box sx={{ mt: 2 }}>
                        {items.map((item, idx) => {
                            const when = item.createdAt || item.timestamp || item.time || item.updatedAt;
                            const who = item.updatedByUserMail || item.updatedBy || item.user || '-';
                            const action = item.action || '-';
                            const summary = item.changeSummary || item.summary || item.message || '-';

                            return (
                                <Paper
                                    key={item.id || idx}
                                    sx={{
                                        p: 2,
                                        mb: 2,
                                        borderRadius: '12px',
                                        cursor: 'pointer',
                                        '&:hover': {
                                            backgroundColor: theme.palette.action.hover
                                        }
                                    }}
                                    onClick={() => handleOpen(item)}
                                >
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                                        <Chip
                                            label={String(action)}
                                            size="small"
                                            color="primary"
                                            variant="outlined"
                                        />
                                        <Typography variant="caption" color="text.secondary">
                                            {when ? new Date(when).toLocaleDateString() : '-'}
                                        </Typography>
                                    </Box>

                                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                                        {summary}
                                    </Typography>

                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Typography variant="caption" color="text.secondary">
                                            By: {String(who)}{item.updatedByRole ? ` (${item.updatedByRole})` : ''}
                                        </Typography>
                                        {when && (
                                            <Typography variant="caption" color="text.secondary">
                                                {new Date(when).toLocaleTimeString()}
                                            </Typography>
                                        )}
                                    </Box>

                                    {(item.version || item.ipAddress) && (
                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                            {item.version ? `Version: ${item.version}` : ''}
                                            {item.ipAddress ? `${item.version ? ' · ' : ''}IP: ${item.ipAddress}` : ''}
                                        </Typography>
                                    )}
                                </Paper>
                            );
                        })}
                    </Box>
                ) : (
                    <TableContainer component={Paper} sx={{ borderRadius: '12px' }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 600 }}>Action</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Change Log</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Updated By</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Created At</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {items.map((item, idx) => {
                                    const when = item.createdAt || item.timestamp || item.time || item.updatedAt;
                                    const who = item.updatedByUserMail || item.updatedBy || item.user || '-';
                                    const action = item.action || '-';
                                    return (
                                        <TableRow
                                            key={item.id || idx}
                                            hover
                                            sx={{ cursor: 'pointer' }}
                                            onClick={() => handleOpen(item)}
                                        >
                                            <TableCell>
                                                <Chip label={String(action)} size="small" color="primary" variant="outlined" />
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                    {getChangeSummary(item.oldJson, item.changeJson)}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {item.version ? `Version: ${item.version}` : ''}
                                                    {item.ipAddress ? `${item.version ? ' · ' : ''}IP: ${item.ipAddress}` : ''}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2">
                                                    {String(who)}{item.updatedByRole ? ` (${item.updatedByRole})` : ''}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2">
                                                    {when ? new Date(when).toLocaleString() : '-'}
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )
            )}

            {/* Details dialog */}
            <Dialog
                open={!!selected}
                onClose={handleClose}
                maxWidth="md"
                fullWidth
                fullScreen={isMobile}
            >
                <DialogTitle>Change Details</DialogTitle>
                <DialogContent dividers>
                    {selected && (
                        <Box>
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1 }}>
                                <Chip label={String(selected.action || '-')} size="small" color="primary" variant="outlined" />
                                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                    {selected.changeSummary || selected.summary || '-'}
                                </Typography>
                            </Box>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                {selected.createdAt ? new Date(selected.createdAt).toLocaleString() : '-'} · {selected.updatedByUserMail || '-'}{selected.updatedByRole ? ` (${selected.updatedByRole})` : ''}
                            </Typography>

                            <Grid container spacing={2} sx={{ maxHeight: isMobile ? 400 : 500, overflow: 'hidden' }}>
                                <Grid item xs={12} md={6} sx={{ height: isMobile ? 200 : 250 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Old</Typography>
                                        <Tooltip title="Copy Old JSON">
                                            <IconButton
                                                size="small"
                                                onClick={() => handleCopy(
                                                    JSON.stringify(safeParseJson(selected?.oldJson), null, 2)
                                                )}
                                            >
                                                <ContentCopy fontSize="inherit" />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                    <Paper
                                        variant="outlined"
                                        sx={{
                                            p: 1.5,
                                            height: isMobile ? 160 : 200,
                                            overflow: 'auto',
                                            border: '1px solid',
                                            borderColor: 'divider'
                                        }}
                                    >
                                        <div style={{
                                            fontSize: isMobile ? '11px' : '13px',
                                            lineHeight: isMobile ? '1.3' : '1.4',
                                            fontFamily: 'monospace'
                                        }}>
                                            {selected.oldJson ? (
                                                <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                                    {JSON.stringify(safeParseJson(selected.oldJson), null, 2)}
                                                </pre>
                                            ) : (
                                                <div style={{ color: '#666', fontStyle: 'italic' }}>
                                                    No previous data (initial creation)
                                                </div>
                                            )}
                                        </div>
                                    </Paper>
                                </Grid>
                                <Grid item xs={12} md={6} sx={{ height: isMobile ? 200 : 250 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>New</Typography>
                                        {selected?.changeJson && (
                                            <Tooltip title="Copy New JSON">
                                                <IconButton
                                                    size="small"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleCopy(JSON.stringify(safeParseJson(selected.changeJson), null, 2));
                                                    }}
                                                >
                                                    <ContentCopy fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                    </Box>

                                    <Paper
                                        variant="outlined"
                                        sx={{
                                            p: 1.5,
                                            height: isMobile ? 160 : 200,
                                            overflow: 'auto',
                                            border: '1px solid',
                                            borderColor: 'divider'
                                        }}
                                    >
                                        <div style={{
                                            fontSize: isMobile ? '11px' : '13px',
                                            lineHeight: isMobile ? '1.3' : '1.4',
                                            fontFamily: 'monospace'
                                        }}>
                                            <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                                {JSON.stringify(safeParseJson(selected.changeJson), null, 2) || '-'}
                                            </pre>
                                        </div>
                                    </Paper>
                                </Grid>

                                {/* Add a diff view section */}
                                <Grid item xs={12}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                                        Changes Summary: {getChangeSummary(selected.oldJson, selected.changeJson)}
                                    </Typography>
                                    <Paper
                                        variant="outlined"
                                        sx={{
                                            p: 1.5,
                                            maxHeight: isMobile ? 150 : 200,
                                            overflow: 'auto',
                                            border: '1px solid',
                                            borderColor: 'divider',
                                            backgroundColor: '#fafafa'
                                        }}
                                    >
                                        <div style={{
                                            fontSize: isMobile ? '11px' : '13px',
                                            lineHeight: isMobile ? '1.3' : '1.4',
                                            fontFamily: 'monospace'
                                        }}>
                                            {computeDiffHtml(selected.oldJson, selected.changeJson)}
                                        </div>
                                    </Paper>
                                </Grid>
                            </Grid>

                            {selected.userAgent && (
                                <Box sx={{ mt: 2 }}>
                                    <Typography variant="caption" color="text.secondary">
                                        User Agent: {selected.userAgent}
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose}>Close</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
